import clsx from 'clsx'
import renderAmountField from 'components/FormControls/amountField'
import renderCheckbox from 'components/FormControls/checkboxField'
import renderSelect from 'components/FormControls/select'
import { Button, Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, getFormValues, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import { Total } from 'modules/Forms/Billing/subcomponents'
import styles from 'modules/Forms/Finance/PromisedPaymentRequest/promised-payment.module.scss'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'

let PromisedPaymentRequestForm = ({
  handleSubmit,
  submitting,
  onClose = () => {},
  form,
  anyTouched,
  change,
  creditSettings: { wallets, min_amount, max_amount, credit_rates, currency },
}) => {
  const { t } = useTranslation()
  const selector = getFormValues(form)
  const pathGen = useGeneratePathI18n()
  const [total, setTotal] = useState({ value: null, currency })

  const {
    formValues: { apply_agreement, amount, credit_period },
    registeredFields,
  } = useSelector((state) => ({
    formValues: selector(state),
    registeredFields: state['form'][form].registeredFields,
  }))

  useEffect(() => {
    const credit_rate = credit_rates.find(
      (cr) =>
        cr.period === credit_period &&
        cr.min_amount <= parseFloat(amount) &&
        parseFloat(amount) <= cr.max_amount,
    )

    change('credit_rate_id', credit_rate?.id)

    const rate = credit_rate?.rate

    setTotal({
      ...total,
      value: amount && rate ? parseFloat(amount) * (1 + rate / 100) : null,
    })
  }, [amount, credit_period, credit_rates])

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) =>
        dispatch({
          meta: { resolve, reject },
          type: 'PROMISED_PAYMENT/CREATE_REQUEST',
          payload: {
            ...omit(values, ['apply_agreement', 'credit_period']),
            amount: parseFloat(values.amount),
          },
        }),
      ).then(onClose),
    [registeredFields],
  )

  return (
    <div className={formStyles['formContainer']}>
      <div className={formStyles['formContainer__inner']}>
        <form onSubmit={handleSubmit(submit)}>
          <Typography variant={'h6'} weight={'regular'}>
            {t('promised_payment::request.header')}
          </Typography>

          <div className={'margin-bottom-16'}>
            <Field
              fullWidth
              name={'wallet_id'}
              showEmptyOption={false}
              component={renderSelect}
              label={t('promised_payment::request.fields.wallet_id.label')}
              options={wallets.map(
                ({
                  id,
                  currency,
                  financial_profile: {
                    financial_profile_type: { type = null } = {},
                    custom_name,
                  } = {},
                }) => ({
                  value: id,
                  label:
                    custom_name ??
                    `${t(`wallets::types.${type}`)} (${t(
                      `common::currencies.${currency}`,
                    )})`,
                }),
              )}
              t={t}
              emptyOptionLabel={t('common::select_empty_option')}
              clearOptionLabel={t('common::select_clear_option')}
            />
          </div>

          <div className={'margin-bottom-16'}>
            <Field
              label={t('promised_payment::request.fields.amount.label')}
              name="amount"
              type="text"
              component={renderAmountField}
              t={t}
              fullWidth
              autocomplete="off"
              format={(value) => (value === null ? '' : '' + value)}
              parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
              helperText={t(
                'promised_payment::request.fields.amount.validation_messages.incorrect_amount_min_max',
                {
                  min_amount,
                  max_amount,
                },
              )}
              InputProps={{
                endAdornment: total.currency && (
                  <Typography variant={'p6'} noMargin color={'text-secondary'}>
                    {total.currency}
                  </Typography>
                ),
              }}
              isAllowed={({ floatValue }) =>
                !max_amount || floatValue <= max_amount
              }
            />
          </div>

          <div className={'margin-bottom-16'}>
            <Field
              label={t('promised_payment::request.fields.rate.label')}
              name="credit_period"
              t={t}
              showEmptyOption={false}
              component={renderSelect}
              normalize={(value = '') => value}
              fullWidth
              readOnly={credit_rates.length === 0}
              options={credit_rates.reduce((acc, rate) => {
                !acc.find((opt) => opt.period === rate.period) &&
                  acc.push({
                    label: t('promised_payment::request.fields.rate.days', {
                      days: '' + rate.period,
                    }),
                    value: rate.period,
                    period: rate.period,
                  })

                return acc
              }, [])}
              emptyOptionLabel={t('common::select_empty_option')}
              clearOptionLabel={t('common::select_clear_option')}
            />
          </div>

          <div className={'margin-bottom-16'} style={{ textAlign: 'right' }}>
            <Total
              payment={total}
              label={t('promised_payment::request.total_label')}
            />
          </div>

          <div className={'margin-bottom-16'}>
            <Typography variant={'p6'}>
              <Field
                name="apply_agreement"
                checked={apply_agreement}
                component={renderCheckbox}
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'promised_payment::request.fields.apply_agreement.label',
                        {
                          url: pathGen(ROUTES.PROMISED_PAYMENT_TERMS),
                        },
                      ),
                    }}
                  />
                }
                t={t}
              />
            </Typography>
          </div>

          <div
            className={clsx(
              formStyles['formControl__action-buttons'],
              styles['action-buttons'],
            )}
          >
            <Button
              disabled={submitting}
              onClick={onClose}
              variant={'ghost'}
              grey
              size="l"
              type="button"
            >
              {t('common::form_buttons.cancel')}
            </Button>
            <Button
              disabled={!anyTouched || submitting}
              size="l"
              variant="primary"
              type="submit"
              bgColor={'Label-GU-Primary'}
            >
              {t('common::form_buttons.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

PromisedPaymentRequestForm = reduxForm({
  form: 'PromisedPaymentRequestForm',
  shouldAsyncValidate: shouldAsyncValidate,
  initialValues: {},
})(PromisedPaymentRequestForm)

PromisedPaymentRequestForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.string,
  anyTouched: PropTypes.bool,
  change: PropTypes.func,
  creditSettings: PropTypes.shape({
    wallets: PropTypes.array,
    credit_rates: PropTypes.array,
    min_amount: PropTypes.number,
    max_amount: PropTypes.number,
    currency: PropTypes.string,
  }),
}

export default PromisedPaymentRequestForm
